import { useState } from 'react';
import { ArrowCounterClockwise, Sparkle, Baby, Spinner } from '@phosphor-icons/react';
import { apiUrl, buildAuthHeaders } from '../config/api';

type Persona = 'new' | 'pcos' | 'endo';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const PERSONAS: { key: Persona; label: string; desc: string; icon: typeof Sparkle }[] = [
  { key: 'new', label: 'New User', desc: 'Blank account, starts at onboarding', icon: Baby },
  { key: 'endo', label: 'Endo', desc: '3 weeks of endometriosis tracking data', icon: Sparkle },
  { key: 'pcos', label: 'PCOS', desc: '3 weeks of PCOS tracking data', icon: Sparkle },
];

export default function DemoResetModal({ isOpen, onClose }: Props) {
  const [loading, setLoading] = useState<Persona | null>(null);

  if (!isOpen) return null;

  const handleReset = async (persona: Persona) => {
    setLoading(persona);
    try {
      const headers = await buildAuthHeaders();
      const res = await fetch(apiUrl('/demo/reset'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ persona }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Reset failed (${res.status})`);
      }

      // Reload the entire app to pick up the new state
      window.location.href = persona === 'new' ? '/' : '/chat';
    } catch (err) {
      console.error('[DemoReset]', err);
      alert(`Reset failed: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-[320px] p-6">
        <div className="flex items-center gap-2 mb-4">
          <ArrowCounterClockwise size={20} className="text-pink-500" weight="bold" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Demo Reset</h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          Choose a persona to reset this account.
        </p>

        <div className="space-y-2">
          {PERSONAS.map(({ key, label, desc, icon: Icon }) => (
            <button
              key={key}
              disabled={loading !== null}
              onClick={() => handleReset(key)}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-pink-300 dark:hover:border-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-colors disabled:opacity-50"
            >
              {loading === key ? (
                <Spinner size={20} className="text-pink-500 animate-spin" />
              ) : (
                <Icon size={20} className="text-pink-500" weight="duotone" />
              )}
              <div className="text-left">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{desc}</div>
              </div>
            </button>
          ))}
        </div>

        <button
          onClick={onClose}
          disabled={loading !== null}
          className="w-full mt-4 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
